import type { CountryPack, QualificationDefault } from './types';
import { pharmacyJobTitleDefaults, pharmacyQualificationDefaults } from './gb-pharmacy';

/*
 * Right to Work is a SYNTHETIC qualification - onboarding creates a row per
 * employee carrying their visa expiry so the gate counts it down through the
 * same L4 to L1 ramp as everything else. Zero grace: illegal employment on
 * day +1. A List A holder (UK passport, ILR) has no expiry and therefore no
 * row. Every GB company needs the type for those rows to hang off, so every
 * sector list carries it: a sector list replaces the baseline, never adds to it.
 */
const rightToWorkUk: QualificationDefault = { name: 'Right to Work (UK)', category: 'regulatory', issuingBody: 'Home Office', validityMonths: null, enforcementLevel: 1, gracePeriodDays: 0, mandatory: true, sectors: [], reminderDays: [90, 30], platformOwned: true };

/**
 * GB — United Kingdom CountryPack.
 *
 * Session 0.5 full build. Every value verified against an official UK
 * source at the date of writing (2026-04-10). Sources cited inline so
 * future maintainers can re-verify without a scavenger hunt:
 *
 *   - gov.uk (NMW, statutory leave, bank holidays, payroll)
 *   - hse.gov.uk (working time, safety qualifications)
 *   - legislation.gov.uk (Employment Rights Act, Equality Act, etc.)
 *   - ico.org.uk (UK GDPR, data protection fees)
 *   - citb.co.uk (CSCS card validity)
 *   - company-information.service.gov.uk (Companies House API)
 *
 * Anything that expires annually (NMW rates, bank holidays, tax thresholds)
 * carries an `effectiveDate` so the freshness check in CountryPackService
 * can catch stale packs in CI.
 */
export const gb: CountryPack = {
  countryCode: 'GB',
  countryName: 'United Kingdom',
  defaultLocale: 'en-GB',
  supportedLocales: ['en-GB'],
  timezone: 'Europe/London',
  awsRegion: 'eu-west-2',

  accountingStandard: 'IFRS',

  currency: {
    code: 'GBP',
    symbol: '£',
    symbolPosition: 'before',
    decimalPlaces: 2,
  },

  formatting: {
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24hr',
    decimalSeparator: '.',
    thousandSeparator: ',',
    weekStartsOn: 1, // Monday
  },

  // ---------------------------------------------------------------------------
  // Tax — HMRC / MTD for VAT
  // ---------------------------------------------------------------------------
  taxSystem: {
    vatGstEnabled: true,
    vatGstName: 'VAT',
    standardRate: 0.2, // 20%
    reducedRates: [
      // Reduced rate 5% — domestic fuel/energy, children's car seats, etc.
      // Source: gov.uk/guidance/rates-of-vat-on-different-goods-and-services
      { name: 'Reduced', rate: 0.05, applies: ['energy', 'domestic_fuel', 'childrens_car_seats'] },
      // Zero-rated — most food, children's clothing, books, exports
      { name: 'Zero', rate: 0.0, applies: ['food', 'childrens_clothing', 'books', 'exports'] },
    ],
    reverseCharge: true, // Construction Industry Domestic Reverse Charge
    taxApiConnector: 'HMRC_MTD', // Making Tax Digital
    taxIdName: 'VAT Number',
    taxIdFormat: '^GB[0-9]{9}$',
    taxReturnFrequency: 'quarterly',
    payrollTaxSystem: 'PAYE', // Pay As You Earn + RTI
  },

  // ---------------------------------------------------------------------------
  // Labour law — Employment Rights Act 1996, Working Time Regulations 1998
  // ---------------------------------------------------------------------------
  labourLaw: {
    // 5.6 weeks statutory = 28 days for a 5-day worker (Working Time Regs 1998)
    annualLeaveDays: 28,
    publicHolidaysExtra: false, // Bank holidays count toward the 28
    publicHolidaysCount: 8, // England & Wales (Scotland has 9, NI has 10)
    weeklyHoursLimit: 48, // Working Time Regs — averaged over 17 weeks
    weeklyHoursOptOut: true, // Signed individual opt-out permitted
    dailyRestMinutes: 660, // 11 hours between shifts
    breakRequiredAfterHours: 6, // 20-minute break when working over 6 hours
    // gov.uk/rest-breaks-work, read 2026-09-28: "Workers have the right to one
    // uninterrupted 20 minute rest break during their working day, if they
    // work more than 6 hours a day." Taken off a day when none of the
    // company's own breaks fell inside the clocked time.
    breakMinutes: 20,
    // gov.uk/rest-breaks-work, read 2026-10-16: workers are entitled to either
    // an uninterrupted 24 hours without any work each week, or an uninterrupted
    // 48 hours without any work each fortnight (WTR 1998 reg 11). Read by the
    // rest warning on the working pattern and the rota.
    weeklyRestHours: 24,
    fortnightlyRestHours: 48,

    minimumWage: {
      type: 'age_banded',
      currency: 'GBP',
      // NMW/NLW always takes effect 1 April. Session 0.5 uses the rates
      // specified in the session prompt for 2026-04-01. Verify against
      // gov.uk/national-minimum-wage-rates before the next annual review.
      effectiveDate: '2026-04-01',
      rates: [
        // National Living Wage — age 21 and over
        { ageMin: 21, hourlyRate: 12.71 },
        // 18 to 20 year old rate
        { ageMin: 18, ageMax: 20, hourlyRate: 10.85 },
        // Under 18 (16-17 years old — under-16s can't legally work full-time)
        { ageMin: 16, ageMax: 17, hourlyRate: 8.0 },
        // Apprentice rate — under 19, or 19+ in the first year of apprenticeship
        { category: 'apprentice', hourlyRate: 6.4 },
      ],
    },

    overtime: {
      // UK has NO statutory overtime premium. Employers can (and often do)
      // contractually agree time-and-a-half or double-time, but there's no
      // legal minimum. Leave disabled at pack level — tenants who offer
      // overtime opt in via their own policy settings.
      enabled: false,
      thresholdHours: 48,
      multiplier: 1.0,
      weeklyOrDaily: 'weekly',
    },

    noticePeriod: {
      afterOneWeek: 1, // 1 week statutory notice after first month
      perYearMax: 12, // Statutory max 12 weeks (12+ years service)
    },

    trialPeriodMaxWeeks: 26, // Common UK probation (6 months)
    redundancyRules: 'Employment Rights Act 1996 Part XI',
    sickPayScheme: 'SSP', // Statutory Sick Pay
  },

  // ---------------------------------------------------------------------------
  // Payment rails — BACS via GoCardless
  // ---------------------------------------------------------------------------
  // HMRC, UK accounts and UK pension files: offered to UK companies only.
  statutoryOutputs: ['hmrc_rti', 'hmrc_payroll_forms', 'hmrc_cis', 'hmrc_vat_return', 'uk_ixbrl', 'nest_export'],

  paymentRails: [
    {
      id: 'bacs_gb',
      name: 'BACS',
      provider: 'gocardless',
      advanceDays: 10, // BACS standard 3-day cycle plus GoCardless pre-notification
      currency: 'GBP',
      countries: ['GB'],
      sandboxAvailable: true,
    },
  ],

  // ---------------------------------------------------------------------------
  // Company registry — Companies House (free REST API)
  // ---------------------------------------------------------------------------
  companyRegistry: {
    name: 'Companies House',
    hasPublicApi: true,
    apiType: 'REST',
    apiBaseUrl: 'https://api.company-information.service.gov.uk',
    lookupByName: true,
    lookupByNumber: true,
    registrationNumberName: 'Company Number',
    freeApi: true,
  },

  // ---------------------------------------------------------------------------
  // Privacy law — UK GDPR + DPA 2018, enforced by the ICO
  // ---------------------------------------------------------------------------
  privacyLaw: {
    lawName: 'UK GDPR',
    regulatorName: 'ICO',
    regulatorUrl: 'https://ico.org.uk',
    registrationRequired: true,
    annualFee: 60, // ICO Tier 2 data protection fee (2026)
    breachNotificationHours: 72,
    breachRecord: {
      purpose: 'This is the record UK GDPR Article 33(5) requires: every personal data breach, its effects and the action taken, whether or not it was reported. The reasoning behind a decision NOT to report is part of that record and is printed with it.',
      reportedTo: 'Reported to the ICO',
      assessBeforeClosing: 'Record whether this breach is reportable to the ICO before closing it',
      assessBeforeReporting: 'Assess this incident as reportable before recording a report to the ICO',
    },
    registers: {
      transferBoundary: 'the UK',
      contractOwed: 'UK GDPR Article 28(3) requires one',
      personalData: 'personal data',
      breachLabel: 'Personal data breach',
      officerLabel: 'Data Protection Officer',
      officerLeadLabel: 'Data protection lead',
      officerAppointed: 'Statutory DPO appointed',
      requiredColumn: 'STATUTORY',
      processingRecord: 'This is the record of processing activities required by UK GDPR Article 30. Each entry says where the personal data comes from, what it passes through, where it ends up, who else receives it, and on what basis it is held.',
      lawfulBasisLabel: 'Lawful basis (Art. 6)',
      sensitiveLabel: 'Special category',
      sensitiveCountLabel: 'Carrying special category data',
      conditionLabel: 'Condition (Art. 9)',
      securityLabel: 'How it is kept safe (Art. 30(1)(g))',
    },
    incidentProcedure: {
      issuedOn: '2026-10-21',
      summary: 'How this company responds to an information security incident, and how it decides whether the incident is a personal data breach that must be reported to the Information Commissioner.',
      lawBasis: 'It is written against UK GDPR Articles 33 and 34 and the Data Protection Act 2018, and against ISO 27001 controls for incident management.',
      personalData: 'personal data',
      breachDefinition: 'A personal data breach is the subset of those incidents that leads to the accidental or unlawful destruction, loss, alteration, unauthorised disclosure of, or access to, personal data. Every personal data breach is an incident; not every incident is a personal data breach.',
      accessRequest: 'A request from an individual about their own data is not an incident: that is a subject access request and follows its own procedure and its own statutory month.',
      clock: [
        'Article 33 gives 72 hours from the moment the company becomes AWARE of a personal data breach - not from when it happened, and not from when it was written down. Awareness means a reasonable degree of certainty that a security incident has occurred that led to personal data being compromised.',
        'The register records that moment as the discovery time, and derives the deadline from it. Logging an incident late does not move the deadline, and correcting the discovery time moves the deadline with it.',
      ],
      recordsKept: 'Article 33(5) requires the company to document every personal data breach - the facts, its effects, and the remedial action taken - whether or not it was reported. The Incidents register is that documentation, including the reasoning behind a decision that a breach was not notifiable.',
      containNote: 'Record what was done and when, on the entry. Containment is the first thing an auditor and the ICO ask about.',
      assess: {
        title: 'Assess whether it is reportable',
        timescale: 'Within 72 hours of awareness, and as early inside that as possible',
        detail: [
          'A personal data breach is reportable to the ICO unless it is unlikely to result in a risk to the rights and freedoms of the people whose data it is. Weigh the kind of data, how much, how many people, how easily they can be identified, and what could happen to them.',
          'Severity is not reportability. A serious incident can be assessed as not notifiable, and a small one can be notifiable. Record the decision AND the reasoning on the entry - the reasoning is itself a record Article 33(5) requires.',
        ],
      },
      notifyRegulator: {
        title: 'Report it to the ICO',
        timescale: 'Within 72 hours of awareness',
        detail: [
          'Report through the ICO personal data breach service at ico.org.uk. Give the nature of the breach, the categories and approximate number of people and records concerned, the likely consequences, the measures taken or proposed, and a contact point.',
          'Where the full facts are not yet known, report what is known inside the 72 hours and send the rest in phases. A late report must say why it was late.',
          'Record the report and the ICO reference on the entry. Only an incident assessed as reportable can be marked as reported: the register refuses the combination, because a report recorded against a breach nobody reported is a false entry on a statutory record.',
        ],
      },
      notifyPeople: {
        title: 'Tell the people affected',
        timescale: 'Without undue delay, where the risk to them is high',
        detail: [
          'Article 34 requires the people whose data it is to be told directly where the breach is likely to result in a high risk to their rights and freedoms. Tell them in plain language: what happened, what it means for them, what we are doing, what they should do, and who to contact.',
          'Record on the entry whether notification was required, and when it was done.',
        ],
      },
    },
    dataSubjectRights: [
      'access',
      'rectification',
      'erasure',
      'portability',
      'restrict',
      'object',
      'automated_decision_making',
    ],
    specialCategoryRules:
      'Article 9 UK GDPR — explicit consent required for biometric template storage',
    crossBorderTransferMechanism: 'IDTA', // International Data Transfer Agreement
  },

  // ---------------------------------------------------------------------------
  // Qualification defaults — pre-loaded when a tenant in this sector signs up
  // ---------------------------------------------------------------------------
  // Nine manufacturing defaults (DoD requires ≥ 5). Safety cards, electrical,
  // working at height, first aid. Validity periods per awarding body rules.
  // ---------------------------------------------------------------------------
  workerTypeDefaults: {
    baseline: [
      { name: 'Workplace', behaviour: 'building' },
      { name: 'Mobile', behaviour: 'site_transport' },
    ],
    construction: [
      { name: 'Office', behaviour: 'building' },
      { name: 'Site', behaviour: 'site_transport' },
      { name: 'Transport', behaviour: 'site_transport' },
    ],
    pharmacy: [
      { name: 'Branch staff', behaviour: 'building' },
      { name: 'Delivery driver', behaviour: 'site_transport' },
    ],
  },
  qualificationDefaults: {
    /*
     * BASELINE — the fallback for any sector without a list of its own
     * (Shane, 2026-08-14).
     *
     * WHAT WENT WRONG WITHOUT IT. seedTenantQualificationTypes reads
     * `qualificationDefaults[sector] ?? []`, and this pack carried
     * `manufacturing` and `construction` only. A tenant on any other sector -
     * office, Healthcare, SAAS, Motor - fell through to the empty array,
     * received ZERO qualification types, and the seeder returned success. The
     * tenant's Setup Journey then read "Missing: Qualification types" with no
     * way to clear it: pressing Finish setup ran the same lookup and did the
     * same nothing.
     *
     * These are the universal UK ones - every employer needs them whatever
     * they trade in. Sector lists stay as they are and are used INSTEAD of
     * this, not on top of it (Shane: manufacturing and construction "dont need
     * it"), so no existing tenant's provisioning changes.
     *
     * NOTE ON `category`: this pack's union is safety | regulatory |
     * professional | driving | health, while seed.ts writes 'awareness' and
     * 'compliance' straight to the database. The same list is therefore mapped
     * onto the pack's categories here - H&S topics to safety, legal duties to
     * regulatory, DSE to health, vehicles to driving.
     */
    baseline: [
      { name: 'Manual Handling',        category: 'safety',     issuingBody: 'HSE',         validityMonths: 36,   enforcementLevel: 2, gracePeriodDays: 14, mandatory: true,  sectors: [], reminderDays: [60, 30, 14] },
      { name: 'Fire Safety',            category: 'safety',     issuingBody: 'HSE',         validityMonths: 12,   enforcementLevel: 2, gracePeriodDays: 14, mandatory: true,  sectors: [], reminderDays: [60, 30, 14] },
      { name: 'H&S Induction',          category: 'safety',     issuingBody: 'HSE',         validityMonths: null, enforcementLevel: 3, gracePeriodDays: 30, mandatory: true,  sectors: [], reminderDays: [30] },
      { name: 'COSHH Awareness',        category: 'safety',     issuingBody: 'HSE',         validityMonths: 36,   enforcementLevel: 3, gracePeriodDays: 30, mandatory: false, sectors: [], reminderDays: [60, 30] },
      { name: 'First Aid',              category: 'safety',     issuingBody: 'HSE',         validityMonths: 36,   enforcementLevel: 1, gracePeriodDays: 14, mandatory: true,  sectors: [], reminderDays: [90, 60, 30] },
      { name: 'Lone Working',           category: 'safety',     issuingBody: 'HSE',         validityMonths: 24,   enforcementLevel: 3, gracePeriodDays: 30, mandatory: false, sectors: [], reminderDays: [60, 30] },
      { name: 'DSE / Workstation',      category: 'health',     issuingBody: 'HSE',         validityMonths: 24,   enforcementLevel: 3, gracePeriodDays: 30, mandatory: false, sectors: [], reminderDays: [60, 30] },
      { name: 'GDPR Awareness',         category: 'regulatory', issuingBody: 'ICO',         validityMonths: 24,   enforcementLevel: 3, gracePeriodDays: 30, mandatory: true,  sectors: [], reminderDays: [60, 30] },
      { name: 'Anti-Bribery',           category: 'regulatory', issuingBody: 'SFO',         validityMonths: 24,   enforcementLevel: 3, gracePeriodDays: 30, mandatory: true,  sectors: [], reminderDays: [60, 30] },
      { name: 'Modern Slavery',         category: 'regulatory', issuingBody: 'HMG',         validityMonths: 24,   enforcementLevel: 3, gracePeriodDays: 30, mandatory: true,  sectors: [], reminderDays: [60, 30] },
      rightToWorkUk,
      { name: 'Vehicle Operator Qual.', category: 'driving',    issuingBody: 'DVSA',        validityMonths: 60,   enforcementLevel: 1, gracePeriodDays: 0,  mandatory: false, sectors: [], reminderDays: [90, 60, 30] },
    ],
    manufacturing: [
      {
        name: 'CSCS Gold Card',
        category: 'safety',
        issuingBody: 'CITB',
        validityMonths: 60,
        enforcementLevel: 1,
        gracePeriodDays: 0,
        mandatory: true,
        sectors: ['manufacturing', 'construction'],
        reminderDays: [90, 30, 14],
      },
      {
        name: 'CSCS Blue Card',
        category: 'safety',
        issuingBody: 'CITB',
        validityMonths: 60,
        enforcementLevel: 1,
        gracePeriodDays: 0,
        mandatory: false,
        sectors: ['manufacturing', 'construction'],
        reminderDays: [90, 30, 14],
      },
      {
        name: 'IPAF (Powered Access)',
        category: 'safety',
        issuingBody: 'IPAF',
        validityMonths: 60,
        enforcementLevel: 1,
        gracePeriodDays: 0,
        mandatory: false,
        sectors: ['manufacturing', 'construction'],
        reminderDays: [90, 30, 14],
      },
      {
        name: 'PASMA (Mobile Towers)',
        category: 'safety',
        issuingBody: 'PASMA',
        validityMonths: 60,
        enforcementLevel: 1,
        gracePeriodDays: 0,
        mandatory: false,
        sectors: ['manufacturing', 'construction'],
        reminderDays: [90, 30, 14],
      },
      {
        name: 'Asbestos Awareness',
        category: 'safety',
        issuingBody: 'HSE Approved',
        validityMonths: 12,
        enforcementLevel: 2,
        gracePeriodDays: 48,
        mandatory: true,
        sectors: ['manufacturing', 'construction'],
        reminderDays: [90, 30, 14],
      },
      {
        name: 'First Aid at Work',
        category: 'safety',
        issuingBody: 'HSE Approved',
        validityMonths: 36,
        enforcementLevel: 2,
        gracePeriodDays: 48,
        mandatory: false,
        sectors: ['manufacturing', 'construction', 'warehouse'],
        reminderDays: [90, 30, 14],
      },
      {
        name: 'Manual Handling',
        category: 'safety',
        issuingBody: 'Internal/Approved',
        validityMonths: 12,
        enforcementLevel: 2,
        gracePeriodDays: 48,
        mandatory: true,
        sectors: ['manufacturing', 'warehouse', 'construction'],
        reminderDays: [90, 30, 14],
      },
      {
        name: '18th Edition Wiring Regulations (BS 7671)',
        category: 'regulatory',
        issuingBody: 'JIB / NICEIC',
        validityMonths: null, // No fixed expiry; refresher recommended on amendment
        enforcementLevel: 1,
        gracePeriodDays: 0,
        mandatory: true,
        sectors: ['manufacturing'],
        reminderDays: [],
      },
      {
        name: 'ECS Gold Card',
        category: 'professional',
        issuingBody: 'JIB',
        validityMonths: 60,
        enforcementLevel: 1,
        gracePeriodDays: 0,
        mandatory: false,
        sectors: ['manufacturing'],
        reminderDays: [90, 30, 14],
      },
      rightToWorkUk,
    ],
    construction: [
      {
        name: 'CSCS Gold Card',
        category: 'safety',
        issuingBody: 'CITB',
        validityMonths: 60,
        enforcementLevel: 1,
        gracePeriodDays: 0,
        mandatory: true,
        sectors: ['construction'],
        reminderDays: [90, 30, 14],
      },
      {
        name: 'Asbestos Awareness',
        category: 'safety',
        issuingBody: 'HSE Approved',
        validityMonths: 12,
        enforcementLevel: 2,
        gracePeriodDays: 48,
        mandatory: true,
        sectors: ['construction'],
        reminderDays: [90, 30, 14],
      },
      {
        name: 'First Aid at Work',
        category: 'safety',
        issuingBody: 'HSE Approved',
        validityMonths: 36,
        enforcementLevel: 2,
        gracePeriodDays: 48,
        mandatory: false,
        sectors: ['construction'],
        reminderDays: [90, 30, 14],
      },
      rightToWorkUk,
    ],

    /* Community pharmacy — 34 types across both nations. Kept in its own
     * file: this list would roughly double gb.ts, and it is the part most
     * certain to churn (technician supervision changes 10 Dec 2026, minimum
     * qualification rises to RQF Level 4 from autumn 2029). */
    pharmacy: [...pharmacyQualificationDefaults, rightToWorkUk],
  },

  jobTitleDefaults: {
    pharmacy: pharmacyJobTitleDefaults,
  },

  // ---------------------------------------------------------------------------
  // Regulatory bodies — for compliance reference URLs and API endpoints
  // ---------------------------------------------------------------------------
  regulatoryBodies: [
    {
      name: 'Health and Safety Executive',
      shortName: 'HSE',
      sector: 'all',
      url: 'https://www.hse.gov.uk',
      freeAccess: true,
    },
    {
      name: 'Construction Industry Training Board',
      shortName: 'CITB',
      sector: 'construction',
      url: 'https://www.citb.co.uk',
      freeAccess: false,
    },
    {
      name: 'Care Quality Commission',
      shortName: 'CQC',
      sector: 'care',
      url: 'https://www.cqc.org.uk',
      freeAccess: true,
    },
    {
      name: 'Financial Conduct Authority',
      shortName: 'FCA',
      sector: 'financial',
      url: 'https://www.fca.org.uk',
      freeAccess: true,
    },
    {
      name: 'Driver and Vehicle Licensing Agency',
      shortName: 'DVLA',
      sector: 'transport',
      url: 'https://www.gov.uk/dvla',
      apiUrl: 'https://driver-vehicle-licensing.api.gov.uk',
      freeAccess: true,
    },
    {
      name: 'Driver and Vehicle Standards Agency',
      shortName: 'DVSA',
      sector: 'transport',
      url: 'https://www.gov.uk/dvsa',
      apiUrl: 'https://developer.mot-testing.service.gov.uk',
      freeAccess: true,
    },
  ],

  // ---------------------------------------------------------------------------
  // Feature flags — which country-specific features are applicable
  // ---------------------------------------------------------------------------
  features: {
    directDebitEnabled: true, // BACS via GoCardless
    companyRegistryApiEnabled: true, // Companies House (free)
    payrollTaxApiEnabled: true, // HMRC RTI + MTD for VAT
    troncSchemeEnabled: true, // Tips Act 2023 — UK (and IE)
    cisSubcontractorEnabled: true, // Construction Industry Scheme
    forsFleetEnabled: true, // Fleet Operator Recognition Scheme
    singleTouchPayrollEnabled: false, // AU only
    fairWorkActEnabled: false, // AU only
    smcrEnabled: true, // FCA Senior Managers & Certification Regime
    consumerDutyEnabled: true, // FCA Consumer Duty
  },

  // ---------------------------------------------------------------------------
  // Bradford Factor thresholds (Session 1.5)
  //
  // Standard HR-software bands used by most UK absence-management products.
  // Matches the visible cutoffs in the HR demo v1_17 Tab 5 Bradford sub-tab
  // (Mitchell 2=Good, Wilson 54=Low, Hartley 128=Monitor, Hughes 225=Trigger).
  // Spec-03 §Tab 5 mentions >150 / >300 as an alternative set; the demo
  // numbers are the de-facto standard and the scope-lock locked these.
  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  // Accident Reporting — RIDDOR 2013
  // Source: hse.gov.uk/riddor · legislation.gov.uk
  // ---------------------------------------------------------------------------
  accidentReporting: {
    regulatorName: 'HSE',
    regulatorUrl: 'https://www.hse.gov.uk/riddor',
    notificationUrl: 'https://www.hse.gov.uk/riddor/report.htm',
    emergencyPhone: '0345 300 9923',
    legislationName: 'RIDDOR 2013',
    legislationRef: 'Reporting of Injuries, Diseases and Dangerous Occurrences Regulations 2013',
    legislationUrl: 'https://www.legislation.gov.uk/uksi/2013/1471/contents/made',
    notifiableEvents: {
      fatal: { mustNotify: true, deadline: 'Immediately by phone, then written within 10 days', immediate: true, reportWithin: { days: 10, working: false } },
      specifiedInjury: { mustNotify: true, deadline: 'Immediately — online or by phone', immediate: true, reportWithin: { days: 10, working: false } },
      overThresholdAbsence: { mustNotify: true, thresholdDays: 7, notifyWithin: 15, deadline: 'Within 15 days of the accident', immediate: false, reportWithin: { days: 15, working: false } },
      dangerousOccurrence: { mustNotify: true, deadline: 'Immediately — online or by phone', immediate: true, reportWithin: { days: 10, working: false } },
      occupationalDisease: { mustNotify: true, deadline: 'As soon as a doctor notifies the employer', immediate: false, reportWithin: { days: 10, working: false } },
    },
  },

  // ---------------------------------------------------------------------------
  // COSHH — Workplace Exposure Limits from EH40 Table 1
  // Source: hse.gov.uk/pUBNS/priced/eh40.pdf
  // All values verified against EH40 (5th Edition, as amended)
  // ---------------------------------------------------------------------------
  coshh: {
    welSource: 'EH40 Table 1 — hse.gov.uk',
    welLimits: [
      {
        substance: 'Xylene (all isomers)',
        casNumber: '1330-20-7',
        welMgM3: 220,
        welPpm: 50,
        stelMgM3: 441,
        stelPpm: 100,
        isCarcinogen: false,
        source: 'EH40 Table 1',
      },
      {
        substance: 'Colophony (rosin) — as resin acid',
        casNumber: '8050-09-7',
        welMgM3: 0.05,
        isCarcinogen: false,
        notes: 'Respiratory sensitiser — annual spirometry recommended',
        source: 'EH40 · HSE SIM 03/2002/26',
      },
      {
        substance: 'Welding fume (mild steel)',
        isCarcinogen: true,
        notes: 'ALARP — no safe level. LEV required for all indoor welding. IARC Group 1 carcinogen. HSE Safety Alert Feb 2019.',
        source: 'HSE Safety Alert Feb 2019 — hse.gov.uk/welding',
      },
      {
        substance: 'Mineral oil mist (pure, severely refined)',
        casNumber: '8042-47-5',
        welMgM3: 5,
        isCarcinogen: false,
        source: 'EH40 Table 1',
      },
      {
        substance: 'Isocyanates (as NCO)',
        casNumber: null,
        welMgM3: 0.02,
        isCarcinogen: false,
        notes: 'Respiratory sensitiser — health surveillance required',
        source: 'EH40 Table 1',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Working Time Regulations 1998 — SI 1998/1833
  // Source: legislation.gov.uk/uksi/1998/1833
  // ---------------------------------------------------------------------------
  wtr: {
    maxWeeklyHours: 48,
    referencePeriodWeeks: 17,
    minDailyRestHours: 11,
    minWeeklyRestHours: 24,
    optOutPermitted: true,
  },

  // ---------------------------------------------------------------------------
  // Young Workers — WTR Reg 6A & 5A (under 18s)
  // Source: gov.uk/employing-young-people, legislation.gov.uk
  // ---------------------------------------------------------------------------
  youngWorker: {
    maxDailyHours: 8,
    maxWeeklyHours: 40,
    minRestBetweenShifts: 12,
    nightStart: '22:00',
    nightEnd: '06:00',
    // gov.uk/rest-breaks-work/young-workers, read 2026-09-28: "a 30 minute
    // rest break if they work more than 4.5 hours".
    breakRequiredAfterHours: 4.5,
    breakMinutes: 30,
  },

  // ---------------------------------------------------------------------------
  // National Minimum Wage — rates by age band
  // Effective 1 April 2026. Source: gov.uk/national-minimum-wage-rates
  // Mirrors the existing labourLaw.minimumWage.rates but structured for
  // the rota publish validator (keyed by band).
  // ---------------------------------------------------------------------------
  nmw: {
    rateByAgeBand: [
      { band: '21_plus', minAge: 21, maxAge: null, hourlyRate: 12.71, effectiveDate: '2026-04-01' },
      { band: '18_to_20', minAge: 18, maxAge: 20, hourlyRate: 10.85, effectiveDate: '2026-04-01' },
      { band: 'under_18', minAge: 16, maxAge: 17, hourlyRate: 8.0, effectiveDate: '2026-04-01' },
      /*
       * 8.00, not 6.40 (corrected 2026-08-24).
       *
       * gov.uk/national-minimum-wage-rates gives the apprentice rate as £8.00
       * from 1 April 2026 - the same as under-18, which is how the two have
       * moved together every year. 6.40 was a prior year's figure.
       *
       * It survived because NMW had no verification stamp, and because nothing
       * in production read it: every consumer read a hardcoded copy in the web
       * payroll module instead. Both halves are now fixed - the stamp is below,
       * and that copy was retired the same day.
       */
      { band: 'apprentice', minAge: null, maxAge: null, hourlyRate: 8.0, effectiveDate: '2026-04-01' },
    ],
  },

  bradfordThresholds: {
    good: 0,
    low: 50,
    monitor: 100,
    trigger: 200,
  },

  // ---------------------------------------------------------------------------
  // Building Maintenance — statutory intervals and Legionella thresholds
  // Source: hse.gov.uk, legislation.gov.uk (see spec-30-building-maintenance-v2_1.md)
  // ---------------------------------------------------------------------------
  buildingMaintenance: {
    statutoryIntervals: {
      fixed_wire_eicr: 60,            // BS 7671 — 5 years commercial
      emergency_lighting: 12,         // BS 5266 — annual full test
      fire_alarm_service: 6,          // BS 5839 — 6-monthly
      fire_extinguishers: 12,         // BS 5306 — annual
      legionella_risk_assessment: 12, // L8 ACOP — annual review
      gas_boiler_cp12: 12,            // Gas Safety Regs 1998 — annual
      pat_testing: null,              // IET Code of Practice — risk-based
    },
    legionellaThresholds: {
      hot_water_calorifier_min: 60,   // ≥ 60°C at calorifier
      hot_water_outlet_min: 50,       // ≥ 50°C within 1 minute
      cold_water_storage_max: 20,     // ≤ 20°C
    },
  },

  // ---------------------------------------------------------------------------
  // Residential Lettings — private rented sector statutory intervals & caps
  // F012 — all figures verified against legislation.gov.uk primary sources:
  //   Gas safety (12 months):
  //     Gas Safety (Installation and Use) Regulations 1998, Reg 36(3)(a)
  //     https://www.legislation.gov.uk/uksi/1998/2451/regulation/36
  //   EICR (5 years / 60 months):
  //     Electrical Safety Standards in the Private Rented Sector (England)
  //     Regulations 2020, Reg 3(2)(a)
  //     https://www.legislation.gov.uk/uksi/2020/312/regulation/3/made
  //   Deposit cap (5 wks / 6 wks at £50k boundary):
  //     Tenant Fees Act 2019, Schedule 1, para 2(3)
  //     https://www.legislation.gov.uk/ukpga/2019/4/schedule/1
  //   Deposit protection (30 days):
  //     Housing Act 2004, s 213(3)
  //     https://www.legislation.gov.uk/ukpga/2004/34/schedule/10
  //   How to Rent guide (required):
  //     Assured Shorthold Tenancies (Notices and Prescribed Requirements)
  //     (England) Regulations 2015 (SI 2015/1646) — Deregulation Act 2015
  //   Right to Rent (required):
  //     Immigration Act 2014, ss 20-37
  //     https://www.legislation.gov.uk/ukpga/2014/22/part/3/chapter/1
  //
  // ENGLAND scope. Scotland / Wales / NI have separate regimes — future
  // country-pack splits will add those (see F041 for devolved-nation packs).
  // ---------------------------------------------------------------------------
  lettings: {
    gasSafetyIntervalMonths: 12,
    eicrIntervalMonths: 60,
    depositCapWeeksLow: 5,
    depositCapWeeksHigh: 6,
    depositRentBandAnnual: 50_000,           // GBP
    depositProtectionDeadlineDays: 30,
    howToRentGuideRequired: true,
    rightToRentCheckRequired: true,
    scope: 'england',
  },

  // ---------------------------------------------------------------------------
  // Fleet & Transport — HGV hours + WTD + team driving + CAZ zones
  // Source: EU Reg 561/2006 retained in UK law · gov.uk/drivers-hours
  // Road Transport (Working Time) Regulations 2005
  // tfl.gov.uk/modes/driving · gov.uk/clean-air-zones (no charge amounts)
  // ---------------------------------------------------------------------------
  fleet: {
    driversHoursLimits: {
      dailyNormal: 9,
      dailyMax: 10,
      dailyExtPerWeek: 2,
      weekly: 56,
      fortnightly: 90,
    },
    teamDrivingDailyLimit: 10,
    teamDrivingVehicleMax: 20,
    continuousLimit: { eu: 4.5, domestic: 5.5, wtd: 6 },
    breakLength: { eu: 45, domestic: 30, wtd: 20 },
    dailyRestNormal: 11,
    weeklyRestNormal: 45,
    cazZones: [
      { name: 'London ULEZ', url: 'https://tfl.gov.uk/modes/driving/ultra-low-emission-zone' },
      { name: 'Birmingham CAZ', url: 'https://www.gov.uk/clean-air-zones' },
      { name: 'Bristol CAZ', url: 'https://www.gov.uk/clean-air-zones' },
      { name: 'Bath CAZ', url: 'https://www.gov.uk/clean-air-zones' },
      { name: 'Sheffield CAZ', url: 'https://www.gov.uk/clean-air-zones' },
      { name: 'Bradford CAZ', url: 'https://www.gov.uk/clean-air-zones' },
    ],
  },

  // ---------------------------------------------------------------------------
  // Plant & Equipment — Session 3.15 (spec-24 v2.1)
  // LOLER 1998 · PUWER 1998 · PSSR 2000 · COSHH 2002 · WAH 2005 · hse.gov.uk
  // Intervals below are HSE ACOP + industry-standard defaults. VERIFY AGAINST
  // CURRENT HSE GUIDANCE BEFORE LAUNCH — LOLER and PSSR both allow written
  // schemes with different intervals for different classes of equipment.
  // ---------------------------------------------------------------------------
  plantEquipment: {
    inspectionIntervals: {
      loler_lifting: 6,
      loler_lifting_accessory: 12,
      puwer_routine: 12,
      pssr_written_scheme: 26,
      coshh_lev_test: 14,
      pat_testing: null,
    },
    regulations: [
      { code: 'LOLER 1998',  name: 'Lifting Operations and Lifting Equipment Regulations 1998', url: 'https://www.legislation.gov.uk/uksi/1998/2307' },
      { code: 'PUWER 1998',  name: 'Provision and Use of Work Equipment Regulations 1998',     url: 'https://www.legislation.gov.uk/uksi/1998/2306' },
      { code: 'PSSR 2000',   name: 'Pressure Systems Safety Regulations 2000',                  url: 'https://www.legislation.gov.uk/uksi/2000/128' },
      { code: 'COSHH 2002',  name: 'Control of Substances Hazardous to Health Regulations 2002', url: 'https://www.legislation.gov.uk/uksi/2002/2677' },
      { code: 'WAH 2005',    name: 'Work at Height Regulations 2005',                           url: 'https://www.legislation.gov.uk/uksi/2005/735' },
    ],
    equipmentTypes: [
      // Lifting and Access
      { category: 'Lifting and Access', type: 'Overhead gantry crane',          primaryRegulations: ['LOLER 1998','PUWER 1998'] },
      { category: 'Lifting and Access', type: 'Jib crane',                       primaryRegulations: ['LOLER 1998','PUWER 1998'] },
      { category: 'Lifting and Access', type: 'Forklift truck',                  primaryRegulations: ['LOLER 1998','PUWER 1998'] },
      { category: 'Lifting and Access', type: 'Pallet truck (electric)',         primaryRegulations: ['PUWER 1998'] },
      { category: 'Lifting and Access', type: 'Telehandler',                     primaryRegulations: ['LOLER 1998','PUWER 1998'] },
      { category: 'Lifting and Access', type: 'MEWP — scissor lift',             primaryRegulations: ['LOLER 1998','PUWER 1998','WAH 2005'] },
      { category: 'Lifting and Access', type: 'MEWP — boom lift',                primaryRegulations: ['LOLER 1998','PUWER 1998','WAH 2005'] },
      { category: 'Lifting and Access', type: 'Chain block',                     primaryRegulations: ['LOLER 1998'] },
      { category: 'Lifting and Access', type: 'Lever hoist',                     primaryRegulations: ['LOLER 1998'] },
      { category: 'Lifting and Access', type: 'Slings and shackles',             primaryRegulations: ['LOLER 1998'] },
      { category: 'Lifting and Access', type: 'Eye bolts',                       primaryRegulations: ['LOLER 1998'] },
      { category: 'Lifting and Access', type: 'Passenger / goods lift',          primaryRegulations: ['LOLER 1998'] },
      // Pressure Systems
      { category: 'Pressure Systems', type: 'Air compressor',                    primaryRegulations: ['PSSR 2000','PUWER 1998'] },
      { category: 'Pressure Systems', type: 'Pressure vessel',                   primaryRegulations: ['PSSR 2000'] },
      { category: 'Pressure Systems', type: 'Steam boiler',                      primaryRegulations: ['PSSR 2000'] },
      { category: 'Pressure Systems', type: 'Autoclave',                         primaryRegulations: ['PSSR 2000'] },
      { category: 'Pressure Systems', type: 'Hydraulic test rig',                primaryRegulations: ['PSSR 2000','PUWER 1998'] },
      { category: 'Pressure Systems', type: 'Gas cylinder manifold',             primaryRegulations: ['PSSR 2000'] },
      // Machinery and Fabrication
      { category: 'Machinery and Fabrication', type: 'Pillar drill',             primaryRegulations: ['PUWER 1998'] },
      { category: 'Machinery and Fabrication', type: 'Bandsaw (metal)',          primaryRegulations: ['PUWER 1998'] },
      { category: 'Machinery and Fabrication', type: 'Bandsaw (wood)',           primaryRegulations: ['PUWER 1998'] },
      { category: 'Machinery and Fabrication', type: 'Lathe',                    primaryRegulations: ['PUWER 1998'] },
      { category: 'Machinery and Fabrication', type: 'Milling machine',          primaryRegulations: ['PUWER 1998'] },
      { category: 'Machinery and Fabrication', type: 'Grinder — bench',          primaryRegulations: ['PUWER 1998'] },
      { category: 'Machinery and Fabrication', type: 'Press brake',              primaryRegulations: ['PUWER 1998'] },
      { category: 'Machinery and Fabrication', type: 'Guillotine (shear)',       primaryRegulations: ['PUWER 1998'] },
      { category: 'Machinery and Fabrication', type: 'CNC machine centre',       primaryRegulations: ['PUWER 1998'] },
      // Welding and Thermal Cutting
      { category: 'Welding and Thermal Cutting', type: 'MIG welding set',        primaryRegulations: ['PUWER 1998','COSHH 2002'] },
      { category: 'Welding and Thermal Cutting', type: 'TIG welding set',        primaryRegulations: ['PUWER 1998','COSHH 2002'] },
      { category: 'Welding and Thermal Cutting', type: 'Stick / arc welder',     primaryRegulations: ['PUWER 1998','COSHH 2002'] },
      { category: 'Welding and Thermal Cutting', type: 'Plasma cutter',          primaryRegulations: ['PUWER 1998','COSHH 2002'] },
      { category: 'Welding and Thermal Cutting', type: 'Oxy-fuel cutting set',   primaryRegulations: ['PUWER 1998','COSHH 2002'] },
      // Electrical
      { category: 'Electrical', type: 'Insulation resistance tester',            primaryRegulations: ['PUWER 1998'] },
      { category: 'Electrical', type: 'Portable appliance (PAT)',                primaryRegulations: ['PUWER 1998'] },
      { category: 'Electrical', type: 'Fixed distribution board',                primaryRegulations: ['PUWER 1998'] },
      { category: 'Electrical', type: 'UPS — datacentre',                        primaryRegulations: ['PUWER 1998'] },
      // Dust, Fume and Ventilation
      { category: 'Dust, Fume and Ventilation', type: 'LEV system — welding',    primaryRegulations: ['COSHH 2002'] },
      { category: 'Dust, Fume and Ventilation', type: 'LEV system — woodshop',   primaryRegulations: ['COSHH 2002'] },
      { category: 'Dust, Fume and Ventilation', type: 'LEV system — spray booth',primaryRegulations: ['COSHH 2002'] },
      { category: 'Dust, Fume and Ventilation', type: 'Dust extraction unit',    primaryRegulations: ['COSHH 2002'] },
      // Conveying and Handling
      { category: 'Conveying and Handling', type: 'Conveyor belt',               primaryRegulations: ['PUWER 1998'] },
      { category: 'Conveying and Handling', type: 'Powered roller table',        primaryRegulations: ['PUWER 1998'] },
      { category: 'Conveying and Handling', type: 'Pallet wrapper',              primaryRegulations: ['PUWER 1998'] },
      // Vehicles and Plant (non-road mobile — road vehicles live in Fleet)
      { category: 'Vehicles and Plant', type: 'Excavator',                       primaryRegulations: ['LOLER 1998','PUWER 1998'] },
      { category: 'Vehicles and Plant', type: 'Dumper',                          primaryRegulations: ['LOLER 1998','PUWER 1998'] },
      { category: 'Vehicles and Plant', type: 'Tracked skid steer',              primaryRegulations: ['PUWER 1998'] },
      { category: 'Vehicles and Plant', type: 'Mobile tower (scaffold)',         primaryRegulations: ['PUWER 1998','WAH 2005'] },
      // Fire and Safety Systems (note: Fire detection/alarm live in Building Maintenance)
      { category: 'Fire and Safety Systems', type: 'Fire extinguisher',          primaryRegulations: ['PUWER 1998'] },
      { category: 'Fire and Safety Systems', type: 'Emergency eye-wash station', primaryRegulations: ['PUWER 1998'] },
      { category: 'Fire and Safety Systems', type: 'Sprinkler valve assembly',   primaryRegulations: ['PUWER 1998'] },
      // Office and Facilities
      { category: 'Office and Facilities', type: 'Commercial shredder',          primaryRegulations: ['PUWER 1998'] },
      { category: 'Office and Facilities', type: 'Paper guillotine',             primaryRegulations: ['PUWER 1998'] },
      { category: 'Office and Facilities', type: 'Catering mixer',               primaryRegulations: ['PUWER 1998'] },
      { category: 'Office and Facilities', type: 'Commercial dishwasher',        primaryRegulations: ['PUWER 1998'] },
      // Specialist
      { category: 'Specialist', type: 'Injection moulding machine',              primaryRegulations: ['PUWER 1998'] },
      { category: 'Specialist', type: 'Laser cutter',                            primaryRegulations: ['PUWER 1998','COSHH 2002'] },
      { category: 'Specialist', type: 'Ultrasonic cleaner',                      primaryRegulations: ['PUWER 1998'] },
      { category: 'Specialist', type: 'Vacuum packer',                           primaryRegulations: ['PUWER 1998'] },
    ],
  },

  // ---------------------------------------------------------------------------
  // ERP & Accounts — Session 3.17 (spec-38 v1.1)
  // 2025-26 tax year values from HMRC.
  // VERIFY AGAINST CURRENT HMRC GUIDANCE BEFORE EVERY ANNUAL REVIEW.
  // Sources:
  //   gov.uk/income-tax-rates
  //   gov.uk/national-insurance-rates-letters
  //   gov.uk/government/publications/rates-and-allowances-national-insurance-contributions
  //   gov.uk/guidance/rates-and-thresholds-for-employers-2025-to-2026
  //   gov.uk/vat-rates
  //   gov.uk/what-is-the-construction-industry-scheme
  // ---------------------------------------------------------------------------
  erpAccounts: {
    taxYear: '2026-27',
    /*
     * VERIFICATION STAMPS — see taxYearGuard.ts for why these exist.
     *
     * Honest per area. SSP and SMP were read off gov.uk on 2026-08-14 and
     * corrected; PAYE, NIC, pension and student loans were NOT, and carry their
     * last known year so the guard names them rather than letting them pass
     * under a blanket claim.
     */
    verification: {
      paye: {
        verifiedForTaxYear: '2026-27',
        verifiedOn: '2026-09-03',
        // 2026-08-15 recorded "unchanged, the allowance and bands are frozen".
        // That was true of the rest of the UK and wrong for Scotland: the
        // starter and basic ceilings were still 2024-25 figures. Re-verified
        // 2026-09-03 against the published rates AND by running HMRC's own
        // 2026-27 test data through the engine — all 168 income-tax vectors
        // reconcile, which checks the values rather than the stamp.
        source: 'gov.uk/guidance/rates-and-thresholds-for-employers-2026-to-2027',
      },
      nic: {
        verifiedForTaxYear: '2026-27',
        verifiedOn: '2026-08-15',
        // Checked 2026-08-15: unchanged. PT £242/wk, UEL £967/wk, ST £96/wk,
        // 8% / 2% employee, 15% employer.
        source: 'gov.uk/national-insurance-rates-letters',
      },
      pension: {
        verifiedForTaxYear: '2026-27',
        verifiedOn: '2026-08-15',
        // Checked 2026-08-15: trigger £10,000 and band £6,240-£50,270 both held
        // for 2026-27 by the Secretary of State.
        source: 'gov.uk/government/publications/review-of-the-automatic-enrolment-earnings-trigger-and-qualifying-earnings-band-for-202627',
      },
      studentLoans: {
        verifiedForTaxYear: '2026-27',
        verifiedOn: '2026-08-15',
        // Checked 2026-08-15: THREE thresholds had moved and were corrected.
        source: 'gov.uk/repaying-your-student-loan/what-you-pay',
      },
      statutoryPay: {
        verifiedForTaxYear: '2026-27',
        verifiedOn: '2026-08-24',
        /*
         * Re-checked 2026-08-24 and FOUR figures were wrong: the maternity,
         * paternity and shared parental weekly rates were all still 187.18
         * (2025-26; now 194.32) and the SMP lower earnings limit was 125
         * (2025-26; now 129). SSP at 123.25 was correct.
         *
         * The 2026-08-14 check missed them because it read
         * gov.uk/employers-maternity-pay-leave, which STILL shows 187.18 and
         * disagrees with HMRC's own rates-and-thresholds guidance. The employer
         * guide pages lag. The rates guidance is the source that governs, and
         * it is now the one cited.
         */
        source:
          'gov.uk/guidance/rates-and-thresholds-for-employers-2026-to-2027, gov.uk/statutory-sick-pay, gov.uk/maternity-pay-leave/pay',
      },
      nmw: {
        verifiedForTaxYear: '2026-27',
        verifiedOn: '2026-08-24',
        // Checked 2026-08-24: 21+ £12.71, 18-20 £10.85, under-18 £8.00 all
        // held; the apprentice rate was a prior year's £6.40 and was corrected
        // to £8.00. NMW had never carried a stamp before today, so the guard
        // could not have named it.
        source: 'gov.uk/national-minimum-wage-rates',
      },
    },
    paye: {
      personalAllowanceAnnual: 12570,
      basicRateThresholdAnnual: 50270,
      higherRateThresholdAnnual: 125140,
      basicRate: 0.20,
      higherRate: 0.40,
      additionalRate: 0.45,
      // Uprated 2026-09-03. These two had been left on Scotland's 2024-25
      // figures (£14,876 / £26,561) while intermediate, higher and advanced
      // were moved on, so HMRC's 2026-27 vectors passed 8 of 30. Published
      // 2026-27 bands of TAXABLE pay are starter to £3,967 and basic to
      // £16,956; +£12,570 of allowance gives the income figures below.
      scottishStarterUpperAnnual: 16537,
      scottishStarterRate: 0.19,
      scottishBasicUpperAnnual: 29526,
      scottishBasicRate: 0.20,
      scottishIntermediateUpperAnnual: 43662,
      scottishIntermediateRate: 0.21,
      scottishHigherUpperAnnual: 75000,
      scottishHigherRate: 0.42,
      scottishAdvancedUpperAnnual: 125140,
      scottishAdvancedRate: 0.45,
      scottishTopRate: 0.48,
    },
    nic: {
      // gov.uk/guidance/rates-and-thresholds-for-employers-2026-to-2027.
      lowerEarningsLimitAnnual: 6708,
      primaryThresholdAnnual: 12570,
      upperEarningsLimitAnnual: 50270,
      secondaryThresholdAnnual: 5000,
      // gov.uk/guidance/rates-and-thresholds-for-employers-2026-to-2027.
      // Held as published, not divided down from the annual figures — see the
      // note on `perPeriod` in types.ts for why no derivation reproduces them.
      perPeriod: {
        /*
         * HMRC PUBLISH WEEKLY, MONTHLY AND ANNUAL — AND NOTHING ELSE.
         *
         * Corrected 2026-09-06 (lanes 1320 and 1329). The two-weekly row here
         * said £193 for the Secondary Threshold, with a comment claiming twice
         * the weekly £96 was wrong and that £193 came from HMRC's own test
         * data. Both halves of that were wrong.
         *
         * CWG2 does not publish a fortnightly table at all. It gives a METHOD:
         * divide the NIC-able earnings by 2, look the result up in the WEEKLY
         * table, multiply the answer by 2. Four-weekly is the same with 4. So
         * the effective thresholds are the weekly figures multiplied — £192
         * fortnightly, £384 four-weekly — and NOT the annual figure divided,
         * which gives £192.31 and £384.62.
         *
         * The earlier note in this file said "do not derive from the annual
         * figures", which was the right conclusion for the wrong reason. The
         * rule is: derive from WEEKLY.
         *
         * `periodNicThresholds` does that multiplication rather than this file
         * holding rows for it — a held row is a fourth and fifth copy of the
         * weekly figures that can fall out of step with them.
         *
         * The exact percentage method is Appendix 1 of HMRC's NI guidance for
         * software developers, 2026-27 v1.1 (24 June 2026). A copy taken before
         * that date is stale.
         *
         * SUPERSEDED 2026-10-02. HMRC's published software examples (payroll
         * test data 2026 to 2027, NI-test-data-examples-from-April-2026-v1-0)
         * put the boundaries at the figures in the fortnightly and four-weekly
         * rows below — ST £193 fortnightly, £385 / £967 / £3,867 four-weekly —
         * and 168 of their cases failed on the weekly multiples. Kris chose
         * HMRC's examples. Each figure below is a boundary case in that
         * workbook (e.g. fortnightly £193.04 → £0.01 employer NIC on A).
         *
         * izust / vust: Investment Zone and veterans upper secondary
         * thresholds. Weekly and monthly from the rates page above; fortnightly
         * and four-weekly from the same HMRC examples (D at £962.03 / £1,924.04).
         */
        weekly: { lel: 129, pt: 242, st: 96, uel: 967, izust: 481, vust: 967 },
        fortnightly: { lel: 258, pt: 484, st: 193, uel: 1934, izust: 962, vust: 1934 },
        fourWeekly: { lel: 516, pt: 967, st: 385, uel: 3867, izust: 1924, vust: 3867 },
        monthly: { lel: 559, pt: 1048, st: 417, uel: 4189, izust: 2083, vust: 4189 },
      },
      investmentZoneUpperSecondaryThresholdAnnual: 25000,
      veteransUpperSecondaryThresholdAnnual: 50270,
      employeeRateCategoryA: 0.08,
      employeeRateCategoryAUpper: 0.02,
      employeeRateDeferred: 0.02,
      employeeRateCategoryBReduced: 0.0185, // married women's reduced main-band rate
      employerRateDefault: 0.15,
      employerRateZero: 0.00,
    },
    pension: {
      qualifyingEarningsLowerAnnual: 6240,
      qualifyingEarningsUpperAnnual: 50270,
      autoEnrolmentTriggerAnnual: 10000,
      minEmployeeContributionRate: 0.05,
      minEmployerContributionRate: 0.03,
      /*
       * PER PAY REFERENCE PERIOD, AS PUBLISHED (2026-10-01). The Pensions
       * Regulator, "Earnings thresholds", 2026-2027 table, read 2026-10-01:
       * 1 week £120 / £192 / £967, fortnight £240 / £384 / £1,934, 4 weeks
       * £480 / £768 / £3,867, 1 month £520 / £833 / £4,189 (lower qualifying
       * earnings / earnings trigger / upper qualifying earnings). Not derived
       * from the annual figures — the 4-week trigger is £768 where £10,000 / 13
       * is £769.23 — so they are held, and `pensionPeriodThresholds` says why.
       */
      perPeriod: {
        weekly: { lower: 120, trigger: 192, upper: 967 },
        fortnightly: { lower: 240, trigger: 384, upper: 1934 },
        fourWeekly: { lower: 480, trigger: 768, upper: 3867 },
        monthly: { lower: 520, trigger: 833, upper: 4189 },
      },
    },
    studentLoans: {
      // 2026-27, read off gov.uk/repaying-your-student-loan/what-you-pay on
      // 2026-08-15. Plans 1, 2 and 4 all moved in April and none had been
      // updated: the pack still held 26065 / 28470 / 32745.
      //
      // A threshold that is TOO LOW takes MORE, so every employee on those
      // plans had been over-repaying since April - roughly £75, £82 and £95 a
      // year respectively, deducted from people who did not owe it.
      //
      // Found by the tax-year guard within minutes of it existing. PAYE, NIC
      // and pension all turned out correct, so nobody would have gone looking.
      plan1ThresholdAnnual: 26900, // was 26065 (2025-26)
      plan2ThresholdAnnual: 29385, // was 28470 (2025-26)
      plan4ThresholdAnnual: 33795, // was 32745 (2025-26)
      plan5ThresholdAnnual: 25000, // unchanged

      postgraduateThresholdAnnual: 21000,
      rateMain: 0.09,
      ratePostgraduate: 0.06,
    },
    statutoryPay: {
      // SSP, as changed on 6 April 2026 by Employment Rights Act 2025 ss.10-13
      // (SI 2026/373). Verified against gov.uk/statutory-sick-pay, not carried
      // over: the previous values here were the 2025-26 figures and the engine
      // was underpaying every tenant on both of them.
      sspWeeklyRate: 123.25,   // was 118.75 (2025-26)
      sspWaitingDays: 0,       // was 3 — abolished, SSP is now a day-one right
      sspEarningsCapPercent: 0.8,
      sspRelevantPeriodWeeks: 8,
      sspMaxWeeks: 28,
      // Self-certification covers the first 7 days; beyond that the employer
      // may require a fit note. gov.uk/taking-sick-leave.
      sspFitNoteAfterDays: 7,
      // SMP eligibility, from gov.uk/employers-maternity-pay-leave/eligibility.
      // The earnings test is EIGHT weeks, not the 52-week reference used for
      // holiday pay, and the £125 limit still applies to SMP even though SSP's
      // was abolished in April 2026.
      smpQualifyingWeeksBeforeEwc: 15,
      smpContinuousEmploymentWeeks: 26,
      smpRelevantPeriodWeeks: 8,
      /*
       * The Lower Earnings Limit, which decides ELIGIBILITY for SMP - not a
       * payment rate, which is why it was easy to miss beside one.
       *
       * £129/week for 2026-27; £125 was 2025-26. Confirmed against the NI bands
       * on gov.uk/national-insurance-rates-letters, where employee NI begins at
       * £129/week at 0%, and against the rates-and-thresholds guidance.
       *
       * Read carefully: £129 is the LEL, NOT the Primary Threshold. The PT -
       * where 8% starts - is £242/week, which is the £12,570 held in nic above.
       * A summary that calls £129 the Primary Threshold is wrong, and getting
       * these two the wrong way round would mis-tax everybody.
       */
      smpLowerEarningsLimitWeekly: 129, // was 125 (2025-26)
      smpProofDeadlineWeeks: 13,
      smpNoticeDaysBeforeSmpStart: 28,
      smpLeaveNoticeWeeksBeforeEwc: 15,
      smp1IssueDays: 7,
      smpRecordRetentionYears: 3,
      smpVeryPrematureWeeks: 15,
      smpStillbirthQualifyingWeek: 24,
      smpHigherRateWeeks: 6,
      /*
       * 194.32, NOT 187.18 (corrected 2026-08-24).
       *
       * 187.18 is the 2025-26 rate. The 2026-27 standard rate is £194.32 from
       * 5 April 2026, and it is the same figure for maternity, paternity and
       * shared parental pay - they move together.
       *
       * The cost of the old figure: £7.14 a week short for up to 33 weeks,
       * £235.62 per maternity, and the same shortfall on the other two.
       *
       * WHY THE 2026-08-14 CHECK MISSED IT, which matters more than the number.
       * gov.uk/employers-maternity-pay-leave - the employer-facing page, and
       * the one cited in the verification stamp below - STILL SHOWS £187.18. It
       * disagrees with HMRC's own rates-and-thresholds guidance and with
       * gov.uk/maternity-pay-leave/pay, both of which give £194.32. Reading one
       * gov.uk page is not verification when gov.uk contradicts itself; the
       * employer guide pages lag the rates guidance.
       *
       * Sources, checked 2026-08-24:
       *   gov.uk/guidance/rates-and-thresholds-for-employers-2026-to-2027
       *   gov.uk/maternity-pay-leave/pay
       */
      smpLowerRateWeekly: 194.32, // was 187.18 (2025-26)
      smpStandardWeeks: 33,
      sppWeeklyRate: 194.32,     // was 187.18 (2025-26) - same statutory rate
      sppMaxWeeks: 2,
      shppWeeklyRate: 194.32,    // was 187.18 (2025-26) - same statutory rate
      spbpWeeklyRate: 194.32,    // same statutory rate as SPP / ShPP
      spbpMaxWeeks: 2,           // Parental Bereavement Leave and Pay Act 2018
      sncpWeeklyRate: 194.32,    // same statutory rate as SPP / ShPP / SPBP
      sncpMaxWeeks: 12,          // Neonatal Care (Leave and Pay) Act 2023
      neonatalCareDaysPerLeaveWeek: 7,    // gov.uk/neonatal-care-pay-leave
      neonatalLeaveWindowWeeks: 68,       // gov.uk/neonatal-care-pay-leave
      parentalBereavementWindowWeeks: 56, // gov.uk/parental-bereavement-pay-leave
      carersLeaveWeeksPer12Months: 1,     // Carer's Leave Regulations 2024
      smpMaxLeaveWeeks: 52,
      smpEarliestLeaveWeeksBeforeEwc: 11,
      sapEarliestLeaveDaysBeforePlacement: 14,
      smpCompulsoryLeaveWeeksAfterBirth: 2,
      smpRecoveryStandard: 0.92,
      // 109%, per gov.uk/employers-maternity-pay-leave/entitlement: "You may be
      // able to reclaim 109% if you qualify for Small Employers' Relief." Was
      // 1.03 — an older rate — so every small employer was under-reclaiming.
      smpRecoverySmallEmployer: 1.09,
    },
    employmentAllowance: {
      annualAmount: 10500,
      eligibilityThresholdPrevYearEmployerNic: 100000,
    },
    apprenticeshipLevy: {
      ratePercent: 0.005,
      annualAllowance: 15000,
      triggerAnnualPayrollBill: 3000000,
    },
    vat: {
      standardRate: 0.20,
      reducedRate: 0.05,
      registrationThreshold: 90000,
      deregistrationThreshold: 88000,
      /*
       * VAT Notice 727. The turnover figures are TAX-EXCLUSIVE annual retail
       * turnover, and every standard scheme stops at £130m — above that a
       * business must agree a bespoke scheme with HMRC rather than choose one.
       * Confirmed against gov.uk on 2026-09-01.
       *
       * Ordered as a shopkeeper would consider them: no scheme, then the
       * simplest, then the two pairs, then bespoke.
       */
      /*
       * WHEN a business accounts for VAT. Confirmed against gov.uk 2026-09-01.
       * The join and leave figures are deliberately different numbers: joining
       * is a choice made once against turnover then; leaving is compulsory and
       * checked on each anniversary, at a higher figure.
       */
      accountingSchemes: [
        {
          key: 'standard',
          name: 'Standard (accrual) VAT accounting',
          notice: 'VAT Notice 700',
          maxTurnoverToJoin: null,
          mustLeaveAboveTurnover: null,
          excludes: [],
          summary:
            'VAT is due on the invoice date, whether or not the customer has '
            + 'paid. The default, and the only option once turnover is large.',
        },
        {
          key: 'cash_accounting',
          name: 'Cash Accounting Scheme',
          notice: 'VAT Notice 731',
          maxTurnoverToJoin: 1_350_000,
          mustLeaveAboveTurnover: 1_600_000,
          excludes: [],
          summary:
            'VAT is due when the customer pays, and reclaimed when you pay your '
            + 'supplier. Helps cash flow when customers are slow, and gives '
            + 'automatic bad-debt relief because unpaid invoices carry no VAT.',
        },
        {
          key: 'flat_rate',
          name: 'Flat Rate Scheme',
          notice: 'VAT Notice 733',
          maxTurnoverToJoin: 150_000,
          mustLeaveAboveTurnover: 230_000,
          /*
           * The Flat Rate Scheme rules out all three of the others, and the
           * reasons differ. HMRC: cash accounting is out because "the Flat Rate
           * Scheme has its own cash-based turnover method"; a retail scheme is
           * out because it "has its own retail based method that is very
           * similar"; and a business using the second-hand margin scheme or the
           * auctioneers' scheme cannot join at all.
           */
          excludes: ['cash_accounting', 'retail_scheme', 'margin_scheme'],
          summary:
            'Pay a fixed percentage of gross turnover and stop reclaiming VAT on '
            + 'most purchases. Simple, and a poor deal for anybody who buys a lot '
            + 'of standard-rated stock.',
        },
      ],

      /*
       * THE FLAT RATE SCHEME'S OWN RULES — VAT Notice 733.
       *
       * VAT-SCHEMES-STORED-AND-IGNORED-1011. `flatRatePercent` was validated
       * 0-20% on the settings form and multiplied by nothing, and the finding
       * described the scheme as "12% of gross turnover". That is the headline
       * and not the scheme: three further rules decide what a business
       * actually pays, and all three change the figure.
       *
       * Here rather than in the service because they are statutory values that
       * move — the limited-cost rate and the goods test arrived in April 2017,
       * and the rates themselves are reviewed. A service that hardcodes them
       * has to be found and edited when they change.
       */
      flatRate: {
        /*
         * A LIMITED COST TRADER pays 16.5% whatever its trade sector says,
         * which is the rule that catches labour-only businesses that joined
         * expecting their sector's rate.
         *
         * The test is on GOODS — not services, and not everything a business
         * buys. Notice 733 §4.6 excludes capital items, food and drink,
         * vehicles and fuel (except for a transport business), and anything
         * bought to give away or resell as a service.
         */
        limitedCostRatePercent: 16.5,
        /** Goods below this share of VAT-inclusive turnover make it limited cost. */
        limitedCostGoodsPercentOfTurnover: 2,
        /** …or below this in a year, whichever bites first. Pro-rated per period. */
        limitedCostGoodsAnnualFloor: 1_000,

        /**
         * One percentage point off in the first year of VAT registration —
         * the registration date, not the date the business joined the scheme.
         */
        firstYearDiscountPercent: 1,

        /*
         * INPUT VAT IS BLOCKED, with one exception: a single purchase of
         * capital goods costing £2,000 or more INCLUDING VAT. That is per
         * purchase rather than per item, so one invoice for six computers
         * totalling £2,400 qualifies and six invoices for £400 do not.
         */
        capitalGoodsReclaimFloorIncVat: 2_000,

        source: 'gov.uk/vat-flat-rate-scheme · VAT Notice 733 §4.4, §4.6, §15',
        verifiedOn: '2026-09-05',
      },

      /*
       * VAT on the margin rather than the price — Notice 718. No turnover limit
       * on any of them, and a business runs them ALONGSIDE ordinary VAT on
       * everything else it sells.
       */
      errorCorrection: {
        flatLimit: 10_000,
        box6Percent: 1,
        upperLimit: 50_000,
        timeLimitYears: 4,
        source: 'gov.uk VAT Notice 700/45 §4.3 (method 1 and method 2), §4.7',
        verifiedOn: '2026-10-28',
      },
      partialExemption: {
        deMinimisMonthly: 625,
        deMinimisShare: 0.5,
        residualRoundUpMonthlyLimit: 400_000,
        source: 'gov.uk VAT Notice 706 §4.3, §4.7 (standard method and its rounding), §4.8 (excluded values), §6.1 (special methods), §11.2, §11.7 (de minimis and simplified tests), §12 (annual adjustment)',
        verifiedOn: '2026-10-31',
      },
      /*
       * Road fuel scale charges: the VAT on private fuel in a business car.
       * Each row: CO2 band (120 is "120 or less", 225 "225 or more"), then the
       * VAT-inclusive charge, its VAT and the VAT-exclusive charge for a 12, a 3
       * and a 1 month period. Used from the first VAT period beginning on or after
       * effectiveFrom. VAT to box 1, the VAT-exclusive charge to box 6 (Notice 700/12
       * §3.2, §3.7).
       */
      fuelScaleCharges: [
        {
          effectiveFrom: '2025-05-01',
          source: 'gov.uk VAT road fuel scale charges from 1 May 2025 to 30 April 2026',
          verifiedOn: '2026-10-31',
          rows: [
            [120, 661, 110.17, 550.83, 164, 27.33, 136.67, 54, 9, 45],
            [125, 990, 165, 825, 248, 41.33, 206.67, 82, 13.67, 68.33],
            [130, 1059, 176.50, 882.50, 263, 43.83, 219.17, 87, 14.50, 72.50],
            [135, 1122, 187, 935, 280, 46.67, 233.33, 93, 15.50, 77.50],
            [140, 1191, 198.50, 992.50, 297, 49.50, 247.50, 99, 16.50, 82.50],
            [145, 1255, 209.17, 1045.83, 313, 52.17, 260.83, 103, 17.17, 85.83],
            [150, 1323, 220.50, 1102.50, 330, 55, 275, 109, 18.17, 90.83],
            [155, 1387, 231.17, 1155.83, 347, 57.83, 289.17, 115, 19.17, 95.83],
            [160, 1456, 242.67, 1213.33, 363, 60.50, 302.50, 120, 20, 100],
            [165, 1520, 253.33, 1266.67, 380, 63.33, 316.67, 126, 21, 105],
            [170, 1588, 264.67, 1323.33, 396, 66, 330, 131, 21.83, 109.17],
            [175, 1652, 275.33, 1376.67, 412, 68.67, 343.33, 137, 22.83, 114.17],
            [180, 1721, 286.83, 1434.17, 429, 71.50, 357.50, 143, 23.83, 119.17],
            [185, 1784, 297.33, 1486.67, 446, 74.33, 371.67, 148, 24.67, 123.33],
            [190, 1853, 308.83, 1544.17, 462, 77, 385, 153, 25.50, 127.50],
            [195, 1917, 319.50, 1597.50, 479, 79.83, 399.17, 159, 26.50, 132.50],
            [200, 1985, 330.83, 1654.17, 496, 82.67, 413.33, 164, 27.33, 136.67],
            [205, 2049, 341.50, 1707.50, 512, 85.33, 426.67, 170, 28.33, 141.67],
            [210, 2118, 353, 1765, 528, 88, 440, 175, 29.17, 145.83],
            [215, 2182, 363.67, 1818.33, 545, 90.83, 454.17, 181, 30.17, 150.83],
            [220, 2250, 375, 1875, 561, 93.50, 467.50, 187, 31.17, 155.83],
            [225, 2314, 385.67, 1928.33, 578, 96.33, 481.67, 192, 32, 160],
          ],
        },
        {
          effectiveFrom: '2026-05-01',
          source: 'gov.uk VAT road fuel scale charges from 1 May 2026 to 30 April 2027',
          verifiedOn: '2026-10-31',
          rows: [
            [120, 657, 109.50, 547.50, 163, 27.17, 135.83, 54, 9, 45],
            [125, 983, 163.83, 819.17, 246, 41, 205, 81, 13.50, 67.50],
            [130, 1051, 175.17, 875.83, 261, 43.50, 217.50, 86, 14.33, 71.67],
            [135, 1114, 185.67, 928.33, 278, 46.33, 231.67, 92, 15.33, 76.67],
            [140, 1182, 197, 985, 294, 49, 245, 98, 16.33, 81.67],
            [145, 1245, 207.50, 1037.50, 311, 51.83, 259.17, 103, 17.17, 85.83],
            [150, 1314, 219, 1095, 328, 54.67, 273.33, 109, 18.17, 90.83],
            [155, 1377, 229.50, 1147.50, 344, 57.33, 286.67, 114, 19, 95],
            [160, 1445, 240.83, 1204.17, 361, 60.17, 300.83, 119, 19.83, 99.17],
            [165, 1508, 251.33, 1256.67, 377, 62.83, 314.17, 125, 20.83, 104.17],
            [170, 1576, 262.67, 1313.33, 393, 65.50, 327.50, 130, 21.67, 108.33],
            [175, 1640, 273.33, 1366.67, 409, 68.17, 340.83, 136, 22.67, 113.33],
            [180, 1708, 284.67, 1423.33, 426, 71, 355, 142, 23.67, 118.33],
            [185, 1771, 295.17, 1475.83, 442, 73.67, 368.33, 146, 24.33, 121.67],
            [190, 1839, 306.50, 1532.50, 459, 76.50, 382.50, 152, 25.33, 126.67],
            [195, 1902, 317, 1585, 475, 79.17, 395.83, 158, 26.33, 131.67],
            [200, 1971, 328.50, 1642.50, 492, 82, 410, 163, 27.17, 135.83],
            [205, 2034, 339, 1695, 509, 84.83, 424.17, 169, 28.17, 140.83],
            [210, 2102, 350.33, 1751.67, 524, 87.33, 436.67, 174, 29, 145],
            [215, 2165, 360.83, 1804.17, 541, 90.17, 450.83, 180, 30, 150],
            [220, 2233, 372.17, 1860.83, 557, 92.83, 464.17, 185, 30.83, 154.17],
            [225, 2297, 382.83, 1914.17, 574, 95.67, 478.33, 190, 31.67, 158.33],
          ],
        },
      ],
      marginSchemeVatFraction: 1 / 6,
      marginSchemes: [
        {
          key: 'second_hand_goods',
          name: 'Margin Scheme — second-hand goods, art and antiques',
          notice: 'VAT Notice 718',
          summary:
            'VAT on the difference between what an item cost and what it sold '
            + 'for, item by item. For goods bought without VAT on them, which is '
            + 'most second-hand stock.',
          neverEligible: [
            'anything you were charged VAT on when you bought it',
            'precious metals',
            'investment gold',
            'precious stones',
          ],
        },
        {
          key: 'global_accounting',
          name: 'Global Accounting',
          notice: 'VAT Notice 718',
          summary:
            'The same idea across a whole VAT period rather than item by item — '
            + 'total eligible sales less total eligible purchases. For high '
            + 'volumes of low-value stock nobody could track individually.',
          neverEligible: [
            'anything you were charged VAT on when you bought it',
            'aircraft, boats and outboard motors',
            'caravans and motor caravans',
            'horses and ponies',
            'motor vehicles, including motor cycles',
          ],
        },
        {
          key: 'second_hand_vehicles',
          name: 'Margin Scheme — second-hand vehicles',
          notice: 'VAT Notice 718/1',
          summary:
            'The used-vehicle version, with its own record-keeping: a stock book '
            + 'per vehicle carrying the purchase and sale details. Global '
            + 'Accounting is not available for vehicles.',
          neverEligible: [
            'anything you were charged VAT on when you bought it',
            'a vehicle bought new',
          ],
        },
        {
          key: 'auctioneers',
          name: "Auctioneers' Scheme",
          notice: 'VAT Notice 718/2',
          summary:
            'For an auctioneer selling in their own name on behalf of somebody '
            + 'else, accounting for VAT on the commission and charges rather '
            + 'than the hammer price.',
          neverEligible: ['anything you were charged VAT on when you bought it'],
        },
      ],

      retailSchemeMinimumMonths: 12,
      retailSchemes: [
        {
          key: 'none',
          name: 'No retail scheme',
          notice: 'VAT Notice 700',
          maxAnnualRetailTurnover: null,
          requiredAboveTurnover: null,
          requiresAnnualStockAdjustment: false,
          requires: ['sales_invoices'],
          summary:
            'VAT worked out from sales invoices. Right for a business that '
            + 'invoices every sale, and wrong for a shop counter, which issues '
            + 'no invoice at all.',
        },
        {
          key: 'point_of_sale',
          name: 'Point of Sale',
          notice: 'VAT Notice 727/3',
          maxAnnualRetailTurnover: 130_000_000,
          requiredAboveTurnover: null,
          requiresAnnualStockAdjustment: false,
          requires: ['rate_split_takings'],
          summary:
            'The till records the VAT rate on every sale as it is rung up. The '
            + 'most accurate scheme, and it needs an EPOS that knows each '
            + "product's rate.",
        },
        {
          key: 'apportionment_1',
          name: 'Apportionment Scheme 1',
          notice: 'VAT Notice 727/4',
          maxAnnualRetailTurnover: 1_000_000,
          requiredAboveTurnover: null,
          requiresAnnualStockAdjustment: true,
          requires: ['purchases_by_rate'],
          summary:
            'Splits takings in the same proportions as the COST of goods bought '
            + 'for resale at each rate. For smaller shops; adjusted once a year.',
        },
        {
          key: 'apportionment_2',
          name: 'Apportionment Scheme 2',
          notice: 'VAT Notice 727/4',
          maxAnnualRetailTurnover: 130_000_000,
          requiredAboveTurnover: null,
          requiresAnnualStockAdjustment: true,
          requires: ['esp_by_rate'],
          summary:
            'Splits takings by the expected SELLING price of goods received, on '
            + 'a rolling 12 months. More work than Scheme 1, and it follows what '
            + 'you actually charge rather than what you paid.',
        },
        {
          key: 'direct_calculation_1',
          name: 'Direct Calculation Scheme 1',
          notice: 'VAT Notice 727/5',
          maxAnnualRetailTurnover: 1_000_000,
          requiredAboveTurnover: null,
          requiresAnnualStockAdjustment: false,
          requires: ['esp_by_rate'],
          summary:
            'Value the smaller class of goods at its expected selling price and '
            + 'take the rest as the other rate. No annual stock adjustment — the '
            + 'usual choice for a pharmacy, where zero-rated dispensing dwarfs '
            + 'the counter.',
        },
        {
          key: 'direct_calculation_2',
          name: 'Direct Calculation Scheme 2',
          notice: 'VAT Notice 727/5',
          maxAnnualRetailTurnover: 130_000_000,
          requiredAboveTurnover: null,
          requiresAnnualStockAdjustment: true,
          requires: ['esp_by_rate'],
          summary:
            'Direct Calculation with an annual stock adjustment, and the minority '
            + 'goods must be the class valued. For a business past the £1m limit '
            + 'on Scheme 1.',
        },
        {
          key: 'bespoke',
          name: 'Bespoke retail scheme',
          notice: 'VAT Notice 727/2',
          maxAnnualRetailTurnover: null,
          requiredAboveTurnover: 130_000_000,
          requiresAnnualStockAdjustment: false,
          requires: ['hmrc_agreement'],
          summary:
            'A method agreed individually with HMRC. Required — not merely '
            + 'available — once retail turnover passes £130m, because no standard '
            + 'scheme may be used above that.',
        },
      ],
    },
    cis: {
      standardDeductionRate: 0.20,
      higherDeductionRate: 0.30,
      grossPaymentRate: 0.00,
    },
    p11d: {
      class1aNicRate: 0.15,
      carFuelBenefitBase: 28200,
      vanBenefitFlat: 4020,
      vanFuelBenefitFlat: 769,
      dieselSupplementPct: 0.04,
      appropriatePctCap: 0.37,
      co2ZeroPct: 0.03,
      co2LowBandPct: 0.16,
      officialRateOfInterest: 0.0375,
      loanDeMinimis: 10000,
      electricRangeBands: [
        { minRangeMiles: 130, pct: 0.03 },
        { minRangeMiles: 70, pct: 0.06 },
        { minRangeMiles: 40, pct: 0.09 },
        { minRangeMiles: 30, pct: 0.13 },
        { minRangeMiles: 0, pct: 0.15 },
      ],
    },
    /*
     * THE NAMESPACE AND TAX YEAR MOVE EVERY APRIL, AND HAD NOT (2026-08-15).
     *
     * These still said 25-26 four months into the 2026-27 tax year. HMRC's
     * "RTI schema changes 2025-26 to 2026-27 v1.0" lists both as changed:
     * the namespace to .../26-27/1 and Related Tax Year (data item 18A) from
     * 25-26 to 26-27. A submission carrying last year's namespace is rejected
     * by the gateway, so every FPS and EPS this platform built would have
     * bounced. Nothing had been submitted, which is the only reason it cost
     * nothing.
     *
     * THIS IS AN ANNUAL JOB. It belongs with the statutory figures the
     * tax-year guard already covers — see `taxYearGuard.ts`. Both strings and
     * the year must be re-read from HMRC's schema-changes note each April.
     *
     * KEPT PER YEAR (2026-10-01). A single namespace sent a 2025-26 FPS in the
     * 26-27 schema, and HMRC's test engine refused it: "[4084] Value
     * '2026-03-31' is too small at ... PmtDate" -- the 26-27 schema's dates
     * start on 6 April 2026. Last year stays here so corrections to it can be
     * filed; each April ADDS a year. Source: HMRC RTI RIM 2026 and 2027 v1.0.
     */
    rti: {
      schemasByTaxYear: {
        '2025-26': {
          fpsNamespace: 'http://www.govtalk.gov.uk/taxation/PAYE/RTI/FullPaymentSubmission/25-26/1',
          epsNamespace: 'http://www.govtalk.gov.uk/taxation/PAYE/RTI/EmployerPaymentSummary/25-26/1',
        },
        '2026-27': {
          fpsNamespace: 'http://www.govtalk.gov.uk/taxation/PAYE/RTI/FullPaymentSubmission/26-27/1',
          epsNamespace: 'http://www.govtalk.gov.uk/taxation/PAYE/RTI/EmployerPaymentSummary/26-27/1',
        },
      },
      testTransactionEngineUrl: 'https://test-transaction-engine.tax.service.gov.uk/submission',
      liveTransactionEngineUrl: 'https://transaction-engine.tax.service.gov.uk/submission',
    },
  },

  // ---------------------------------------------------------------------------
  // Bank holidays — UK official list from gov.uk/bank-holidays
  // England & Wales only. Scotland has different dates for some holidays;
  // Northern Ireland adds St Patrick's Day (17 Mar) and the Twelfth (12 Jul).
  //
  // PRE-004 — Bank holidays require annual refresh per
  // docs/procedures/country-pack-bank-holiday-refresh.md.
  // CI-safety test in country-packs/src/index.test.ts asserts
  // coverage for the current + next calendar year.
  // Source: https://www.gov.uk/bank-holidays.json (England and Wales division)
  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  // Employee tax identifier — the National Insurance number (TODO 084)
  //
  // Source: HMRC National Insurance Manual NIM39110.
  //   "A NINO is made up of 2 letters, 6 numbers and a final letter, which is
  //    always A, B, C, or D."
  //   "The characters D, F, I, Q, U, and V are not used as either the first or
  //    second letter of a NINO prefix. The letter O is not used as the second
  //    letter of a prefix."
  //   "Prefixes BG, GB, KN, NK, NT, TN and ZZ are not to be used"
  //
  // Each clause of the pattern below is one of those sentences, in order.
  //
  // WHAT THIS DELIBERATELY DOES NOT DO: it does not require a number. An
  // employee can legitimately not have one yet — a new starter waiting on
  // theirs, for instance — and HMRC's own guidance is to leave the field blank
  // rather than invent a value. Blank stays acceptable; a value that is present
  // has to be a real shape.
  // ---------------------------------------------------------------------------
  bankDetails: {
    routingName: 'Sort code',
    routingGroups: [2, 2, 2],
    accountName: 'Account number',
    accountStrip: '[^0-9]',
    accountFormat: '^[0-9]{6,8}$',
    accountHint: '6-8 digits',
    routingRule: 'A sort code is six digits.',
    accountRule: 'An account number is six to eight digits.',
    // The Bacs and Bankline files carry eight.
    payableAccountFormat: '^[0-9]{8}$',
    payableAccountRule: 'An account number is eight digits.',
  },

  employeeTaxId: {
    name: 'National Insurance number',
    // (?!...) the seven unallocated prefixes · first letter · second letter
    // (O also excluded) · six digits · suffix A-D
    format: '^(?!BG|GB|KN|NK|NT|TN|ZZ)[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z][0-9]{6}[A-D]$',
    strip: '[\\s-]',
    uppercase: true,
    /*
     * NOT 'QQ123456A', and the reason is worth keeping.
     *
     * QQ is the example HMRC and gov.uk print everywhere — precisely BECAUSE it
     * can never be issued: Q is one of the letters forbidden in a prefix, so the
     * conventional example is deliberately an INVALID number. Using it here
     * would have produced an error message telling somebody to type a value
     * this same rule then refuses. Caught by `ni-number-format.test.ts`.
     *
     * This is a format illustration only. The digits are fictional and no
     * example can be guaranteed unissued while still being well formed — which
     * is the whole reason HMRC reach for an invalid one.
     */
    example: 'AB123456C',
  },

  bankHolidays: {
    /*
     * THE TABLE REACHES BACKWARDS, AND THAT IS THE POINT (added 2026-09-24).
     *
     * It used to start at 2026. `actExcludedDays` warns rather than throws for
     * a year it does not hold, so a Construction Act deadline computed across
     * 2025 counted bank holidays as ordinary days, landed EARLY, and reported a
     * pay-less notice as out of time while the payer was still within it. Two
     * integration tests caught that. A contractor would not have — they would
     * have seen a date and believed it.
     *
     * s116 HGCRA excludes Christmas Day, Good Friday and bank holidays from
     * statutory periods, and applications under a live sub-contract are
     * routinely a year or two old by the time anyone argues about them. So the
     * table has to reach back as far as it reaches forward. 2019 is where
     * gov.uk's own feed starts; that is the floor, and it is theirs, not a
     * judgement of ours.
     *
     * CHRISTMAS DAY IS DELIBERATELY MISSING FROM SOME YEARS. Where 25 December
     * falls at a weekend, gov.uk lists only the SUBSTITUTE (2021, 2022, 2027),
     * so the 25th is genuinely absent here for those years. s116 excludes
     * Christmas Day ITSELF whatever day it falls on, and `s116ExcludedDays`
     * adds it back. Do not "correct" the gap in this table — the two rules are
     * different and each is right in its own place.
     *
     * Every date verified against gov.uk/bank-holidays.json, england-and-wales
     * division, on 2026-09-24.
     */
    2019: [
      '2019-01-01', // New Year's Day
      '2019-04-19', // Good Friday
      '2019-04-22', // Easter Monday
      '2019-05-06', // Early May bank holiday
      '2019-05-27', // Spring bank holiday
      '2019-08-26', // Summer bank holiday
      '2019-12-25', // Christmas Day
      '2019-12-26', // Boxing Day
    ],
    2020: [
      '2020-01-01', // New Year's Day
      '2020-04-10', // Good Friday
      '2020-04-13', // Easter Monday
      '2020-05-08', // Early May bank holiday — MOVED to the Friday for VE Day 75
      '2020-05-25', // Spring bank holiday
      '2020-08-31', // Summer bank holiday
      '2020-12-25', // Christmas Day
      '2020-12-28', // Boxing Day (substitute — 26 Dec is a Saturday)
    ],
    2021: [
      '2021-01-01', // New Year's Day
      '2021-04-02', // Good Friday
      '2021-04-05', // Easter Monday
      '2021-05-03', // Early May bank holiday
      '2021-05-31', // Spring bank holiday
      '2021-08-30', // Summer bank holiday
      '2021-12-27', // Christmas Day (substitute — 25 Dec is a Saturday)
      '2021-12-28', // Boxing Day (substitute — 26 Dec is a Sunday)
    ],
    2022: [
      '2022-01-03', // New Year's Day (substitute — 1 Jan is a Saturday)
      '2022-04-15', // Good Friday
      '2022-04-18', // Easter Monday
      '2022-05-02', // Early May bank holiday
      '2022-06-02', // Spring bank holiday — MOVED for the Platinum Jubilee
      '2022-06-03', // Platinum Jubilee bank holiday (one-off)
      '2022-08-29', // Summer bank holiday
      '2022-09-19', // State Funeral of Queen Elizabeth II (one-off)
      '2022-12-26', // Boxing Day
      '2022-12-27', // Christmas Day (substitute — 25 Dec is a Sunday)
    ],
    2023: [
      '2023-01-02', // New Year's Day (substitute — 1 Jan is a Sunday)
      '2023-04-07', // Good Friday
      '2023-04-10', // Easter Monday
      '2023-05-01', // Early May bank holiday
      '2023-05-08', // Coronation of King Charles III (one-off)
      '2023-05-29', // Spring bank holiday
      '2023-08-28', // Summer bank holiday
      '2023-12-25', // Christmas Day
      '2023-12-26', // Boxing Day
    ],
    2024: [
      '2024-01-01', // New Year's Day
      '2024-03-29', // Good Friday
      '2024-04-01', // Easter Monday
      '2024-05-06', // Early May bank holiday
      '2024-05-27', // Spring bank holiday
      '2024-08-26', // Summer bank holiday
      '2024-12-25', // Christmas Day
      '2024-12-26', // Boxing Day
    ],
    2025: [
      '2025-01-01', // New Year's Day
      '2025-04-18', // Good Friday
      '2025-04-21', // Easter Monday
      '2025-05-05', // Early May bank holiday
      '2025-05-26', // Spring bank holiday
      '2025-08-25', // Summer bank holiday
      '2025-12-25', // Christmas Day
      '2025-12-26', // Boxing Day
    ],
    // 2026 — England & Wales, verified against gov.uk
    2026: [
      '2026-01-01', // New Year's Day
      '2026-04-03', // Good Friday
      '2026-04-06', // Easter Monday
      '2026-05-04', // Early May bank holiday
      '2026-05-25', // Spring bank holiday
      '2026-08-31', // Summer bank holiday
      '2026-12-25', // Christmas Day
      '2026-12-28', // Boxing Day (substitute — 26 Dec is a Saturday)
    ],
    // 2027 — England & Wales, verified against gov.uk/bank-holidays.json
    // (PRE-004 annual refresh)
    2027: [
      '2027-01-01', // New Year's Day
      '2027-03-26', // Good Friday
      '2027-03-29', // Easter Monday
      '2027-05-03', // Early May bank holiday
      '2027-05-31', // Spring bank holiday
      '2027-08-30', // Summer bank holiday
      '2027-12-27', // Christmas Day (substitute — 25 Dec is a Saturday)
      '2027-12-28', // Boxing Day (substitute — 26 Dec is a Sunday)
    ],
    // 2028 — England & Wales, verified against gov.uk/bank-holidays.json
    // 2026-09-04. THIS IS THE LAST YEAR GOV.UK PUBLISHES, so it is also the
    // edge of what this table can honestly hold — see the note below.
    2028: [
      '2028-01-03', // New Year's Day (substitute — 1 Jan is a Saturday)
      '2028-04-14', // Good Friday
      '2028-04-17', // Easter Monday
      '2028-05-01', // Early May bank holiday
      '2028-05-29', // Spring bank holiday
      '2028-08-28', // Summer bank holiday
      '2028-12-25', // Christmas Day
      '2028-12-26', // Boxing Day
    ],
  },

  // gov.uk/bank-holidays.json, England and Wales: each date above by its title.
  bankHolidayNames: {
    '2019-01-01': "New Year's Day",
    '2019-04-19': 'Good Friday',
    '2019-04-22': 'Easter Monday',
    '2019-05-06': 'Early May bank holiday',
    '2019-05-27': 'Spring bank holiday',
    '2019-08-26': 'Summer bank holiday',
    '2019-12-25': 'Christmas Day',
    '2019-12-26': 'Boxing Day',
    '2020-01-01': "New Year's Day",
    '2020-04-10': 'Good Friday',
    '2020-04-13': 'Easter Monday',
    '2020-05-08': 'Early May bank holiday (VE day)',
    '2020-05-25': 'Spring bank holiday',
    '2020-08-31': 'Summer bank holiday',
    '2020-12-25': 'Christmas Day',
    '2020-12-28': 'Boxing Day (substitute day)',
    '2021-01-01': "New Year's Day",
    '2021-04-02': 'Good Friday',
    '2021-04-05': 'Easter Monday',
    '2021-05-03': 'Early May bank holiday',
    '2021-05-31': 'Spring bank holiday',
    '2021-08-30': 'Summer bank holiday',
    '2021-12-27': 'Christmas Day (substitute day)',
    '2021-12-28': 'Boxing Day (substitute day)',
    '2022-01-03': "New Year's Day (substitute day)",
    '2022-04-15': 'Good Friday',
    '2022-04-18': 'Easter Monday',
    '2022-05-02': 'Early May bank holiday',
    '2022-06-02': 'Spring bank holiday',
    '2022-06-03': 'Platinum Jubilee bank holiday',
    '2022-08-29': 'Summer bank holiday',
    '2022-09-19': 'Bank Holiday for the State Funeral of Queen Elizabeth II',
    '2022-12-26': 'Boxing Day',
    '2022-12-27': 'Christmas Day (substitute day)',
    '2023-01-02': "New Year's Day (substitute day)",
    '2023-04-07': 'Good Friday',
    '2023-04-10': 'Easter Monday',
    '2023-05-01': 'Early May bank holiday',
    '2023-05-08': 'Bank holiday for the coronation of King Charles III',
    '2023-05-29': 'Spring bank holiday',
    '2023-08-28': 'Summer bank holiday',
    '2023-12-25': 'Christmas Day',
    '2023-12-26': 'Boxing Day',
    '2024-01-01': "New Year's Day",
    '2024-03-29': 'Good Friday',
    '2024-04-01': 'Easter Monday',
    '2024-05-06': 'Early May bank holiday',
    '2024-05-27': 'Spring bank holiday',
    '2024-08-26': 'Summer bank holiday',
    '2024-12-25': 'Christmas Day',
    '2024-12-26': 'Boxing Day',
    '2025-01-01': "New Year's Day",
    '2025-04-18': 'Good Friday',
    '2025-04-21': 'Easter Monday',
    '2025-05-05': 'Early May bank holiday',
    '2025-05-26': 'Spring bank holiday',
    '2025-08-25': 'Summer bank holiday',
    '2025-12-25': 'Christmas Day',
    '2025-12-26': 'Boxing Day',
    '2026-01-01': "New Year's Day",
    '2026-04-03': 'Good Friday',
    '2026-04-06': 'Easter Monday',
    '2026-05-04': 'Early May bank holiday',
    '2026-05-25': 'Spring bank holiday',
    '2026-08-31': 'Summer bank holiday',
    '2026-12-25': 'Christmas Day',
    '2026-12-28': 'Boxing Day (substitute day)',
    '2027-01-01': "New Year's Day",
    '2027-03-26': 'Good Friday',
    '2027-03-29': 'Easter Monday',
    '2027-05-03': 'Early May bank holiday',
    '2027-05-31': 'Spring bank holiday',
    '2027-08-30': 'Summer bank holiday',
    '2027-12-27': 'Christmas Day (substitute day)',
    '2027-12-28': 'Boxing Day (substitute day)',
    '2028-01-03': "New Year's Day (substitute day)",
    '2028-04-14': 'Good Friday',
    '2028-04-17': 'Easter Monday',
    '2028-05-01': 'Early May bank holiday',
    '2028-05-29': 'Spring bank holiday',
    '2028-08-28': 'Summer bank holiday',
    '2028-12-25': 'Christmas Day',
    '2028-12-26': 'Boxing Day',
  },
};
